import { Grid } from "@mui/material";
import { SecurityPipelineGithubPageTabStats } from "../SecurityPipelineGithubPageStats/SecurityPipelineGithubPageStats";
import { useScope } from "~/hooks/useScope";
import { useMetrics } from "~/hooks";
import { Metrics } from "~/lib/arrow";
import { SecurityPipelineGithubStatsSkeleton } from "./SecurityPipelineGithubStatsSkeleton";
import { useParams } from "react-router-dom";
import { roundMinutesToNearestHalf } from "~/lib/date";
import { getIntegrationMrn } from "~/pages/integrations/utils/integrationLinksUtils";

interface SecurityPipelineGithubStatsProps {
  animationKey: number;
  showParticles?: boolean;
  onReplay?: () => void;
}

export const SecurityPipelineGithubStats = ({
  animationKey,
  showParticles = false,
  onReplay,
}: SecurityPipelineGithubStatsProps) => {
  const { activeScope } = useScope();
  const { integrationId } = useParams();
  const integrationMrn = activeScope
    ? getIntegrationMrn({ scope: activeScope, integrationId })
    : "";

  const { securityPipelineGithubStats, isLoading } = useMetrics({
    entityMrn: activeScope?.mrn || "",
    metricMrns: [Metrics.SecurityPipelineGithubStats],
    key: integrationMrn,
    skip: !activeScope || !integrationId,
  });

  const manualMinutesNum = securityPipelineGithubStats.manualMinutes;
  const mondooMinutesNum = securityPipelineGithubStats.mondooMinutes;
  const findingsTotalCountNum = securityPipelineGithubStats.findingsTotalCount;

  const hoursManually = roundMinutesToNearestHalf(manualMinutesNum);
  const hoursMondoo = roundMinutesToNearestHalf(mondooMinutesNum);
  const timesFaster =
    hoursMondoo > 0
      ? parseFloat((hoursManually / hoursMondoo).toFixed(1))
      : NaN;
  const isValidTimesFaster = !isNaN(timesFaster);
  const fasterPercentage =
    ((hoursManually - hoursMondoo) / hoursManually) * 100;

  return (
    <Grid
      container
      spacing={2}
      id="sp-github-stats"
      data-testid="sp-github-stats"
    >
      {isLoading ? (
        <SecurityPipelineGithubStatsSkeleton />
      ) : (
        <>
          <Grid size={{ xs: 12, md: 4, lg: 4 }}>
            <SecurityPipelineGithubPageTabStats
              total={findingsTotalCountNum}
              totalTarget="Total fixes automated"
              animationKey={animationKey}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4, lg: 4 }}>
            <SecurityPipelineGithubPageTabStats
              total={hoursManually}
              totalTarget="hours manually"
              animationKey={animationKey}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4, lg: 4 }}>
            <SecurityPipelineGithubPageTabStats
              total={hoursMondoo}
              totalTarget="hours with Mondoo"
              animationKey={animationKey}
              {...(isValidTimesFaster && { timesFaster: fasterPercentage })}
              showParticles={showParticles}
              onReplay={onReplay}
            />
          </Grid>
        </>
      )}
    </Grid>
  );
};
